import { useState, useEffect } from 'react'
import { useGetAgentSafetyConfigQuery, useUpdateAgentSafetyConfigMutation } from '../../store/api/agentsApi'
import { Button, Alert, Textarea } from '../../components/ui'
import { COLOR_SUCCESS, ADMIN_SAVED_BANNER_TIMEOUT_MS, MESSAGES } from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminAgentSafety() {
  const { data, isFetching, refetch } = useGetAgentSafetyConfigQuery()
  const [updateConfig, { isLoading: saving }] = useUpdateAgentSafetyConfigMutation()

  const [preamble, setPreamble] = useState('')
  const [reminder, setReminder] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data?.config) { setPreamble(data.config.preamble); setReminder(data.config.reminder) }
  }, [data?.config])

  const savedPreamble = data?.config?.preamble ?? ''
  const savedReminder = data?.config?.reminder ?? ''
  const isDirty = preamble !== savedPreamble || reminder !== savedReminder

  const handleSave = async () => {
    setSaveError('')
    try {
      await updateConfig({ preamble, reminder }).unwrap()
      setSaved(true); setTimeout(() => setSaved(false), ADMIN_SAVED_BANNER_TIMEOUT_MS)
    } catch { setSaveError(MESSAGES.SAVE_FAILED) }
  }

  const handleResetToDefault = () => {
    if (!data?.defaults) return
    setPreamble(data.defaults.preamble)
    setReminder(data.defaults.reminder)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>🛡️ Agent Safety</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
            Platform-level rules prepended and appended around every agent's own persona — PII, platform internals, and boundary enforcement.
            Applies to Ekkam-run agents (internal + hosted) only; webhook-based external agents run on the vendor's own server and can't be reached by this.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isDirty && !saving && <span className={s.unsavedLabel}>Unsaved changes</span>}
          {saved  && <span className={s.savedLabel} style={{ color: COLOR_SUCCESS }}>✓ Saved</span>}
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
          <Button onClick={handleResetToDefault} variant="ghost" disabled={isFetching}>Reset to default</Button>
          <Button onClick={() => void handleSave()} disabled={!isDirty} loading={saving} style={{ opacity: !isDirty ? 0.5 : 1 }}>Save</Button>
        </div>
      </div>

      {saveError && <Alert variant="error" style={{ marginBottom: '1rem' }}>{saveError}</Alert>}

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.875rem 1rem', marginBottom: '0.875rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>Preamble</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
          Prepended before the agent's own persona — establishes the platform rules take priority. The agent's persona is appended right after this.
        </div>
        <Textarea
          value={preamble}
          onChange={e => setPreamble(e.target.value)}
          rows={16}
          style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', lineHeight: 1.5, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.875rem 1rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>Reminder</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
          Appended after everything else in the system prompt (persona, poll context, knowledge base chunks) — stays close to the
          actual generation point in multi-turn conversations, where escalation attempts tend to happen.
        </div>
        <Textarea
          value={reminder}
          onChange={e => setReminder(e.target.value)}
          rows={6}
          style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', lineHeight: 1.5, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
        />
      </div>
    </div>
  )
}
